import { useState, useRef, useEffect, forwardRef, useImperativeHandle } from 'react';
import { imagesApi, processingApi } from '../services/api';

type Step = 'upload' | 'operation' | 'options' | 'processing' | 'output';
type Operation = 'coloring' | 'cleanup' | 'watermark';
type Detail = 'simple' | 'standard' | 'detailed';
type MaskHandle = { getMask: () => Promise<Blob | null> };

const OPERATIONS: { id: Operation; title: string; blurb: string; emoji: string }[] = [
  { id: 'coloring', title: 'Coloring page', blurb: 'Turn a photo into a coloring page.', emoji: '🎨' },
  { id: 'cleanup', title: 'Clean up a drawing', blurb: 'Tidy an existing line drawing.', emoji: '🧹' },
  { id: 'watermark', title: 'Remove watermark', blurb: 'Paint over a watermark to erase it.', emoji: '🧽' },
];

const DETAILS: { id: Detail; title: string; blurb: string; emoji: string }[] = [
  { id: 'simple', title: 'Simple', blurb: 'Bold, chunky lines. Great for toddlers.', emoji: '🐣' },
  { id: 'standard', title: 'Standard', blurb: 'Balanced. Good for most kids.', emoji: '🎨' },
  { id: 'detailed', title: 'Detailed', blurb: 'Finer lines. Older kids & adults.', emoji: '✨' },
];

const PAPERS = ['A4', 'A3', 'A5', 'Letter', 'Tabloid'];

// --- Paint-the-mask canvas (for watermark removal) ---
const MaskPainter = forwardRef<MaskHandle, { imageUrl: string }>(({ imageUrl }, ref) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const drawing = useRef(false);
  const [dims, setDims] = useState({ w: 480, h: 360 });

  useEffect(() => {
    const img = new Image();
    img.onload = () => {
      const scale = Math.min(1, 480 / img.width);
      setDims({ w: Math.round(img.width * scale), h: Math.round(img.height * scale) });
    };
    img.src = imageUrl;
  }, [imageUrl]);

  const paint = (e: React.PointerEvent) => {
    if (!drawing.current) return;
    const c = canvasRef.current;
    if (!c) return;
    const rect = c.getBoundingClientRect();
    const x = (e.clientX - rect.left) * (c.width / rect.width);
    const y = (e.clientY - rect.top) * (c.height / rect.height);
    const ctx = c.getContext('2d')!;
    ctx.fillStyle = 'rgba(255,80,80,0.55)';
    ctx.beginPath();
    ctx.arc(x, y, 16, 0, Math.PI * 2);
    ctx.fill();
  };

  useImperativeHandle(ref, () => ({
    getMask: () =>
      new Promise<Blob | null>((resolve) => {
        const c = canvasRef.current;
        if (!c) return resolve(null);
        const m = document.createElement('canvas');
        m.width = c.width;
        m.height = c.height;
        const mctx = m.getContext('2d')!;
        mctx.fillStyle = 'black';
        mctx.fillRect(0, 0, m.width, m.height);
        const src = c.getContext('2d')!.getImageData(0, 0, c.width, c.height);
        const out = mctx.getImageData(0, 0, m.width, m.height);
        for (let i = 0; i < src.data.length; i += 4) {
          if (src.data[i + 3] > 10) {
            out.data[i] = 255;
            out.data[i + 1] = 255;
            out.data[i + 2] = 255;
            out.data[i + 3] = 255;
          }
        }
        mctx.putImageData(out, 0, 0);
        m.toBlob((b) => resolve(b), 'image/png');
      }),
  }));

  return (
    <div className="relative mx-auto rounded-lg overflow-hidden border" style={{ width: dims.w, height: dims.h }}>
      <img src={imageUrl} alt="" className="absolute inset-0 w-full h-full object-contain pointer-events-none select-none" />
      <canvas
        ref={canvasRef}
        width={dims.w}
        height={dims.h}
        className="absolute inset-0 w-full h-full touch-none cursor-crosshair"
        onPointerDown={(e) => {
          drawing.current = true;
          paint(e);
        }}
        onPointerMove={paint}
        onPointerUp={() => (drawing.current = false)}
        onPointerLeave={() => (drawing.current = false)}
      />
    </div>
  );
});

export function CreatePage() {
  const [step, setStep] = useState<Step>('upload');
  const [file, setFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState('');
  const [op, setOp] = useState<Operation>('coloring');
  const [detail, setDetail] = useState<Detail>('standard');
  const [resultId, setResultId] = useState<number | null>(null);
  const [resultUrl, setResultUrl] = useState('');
  const [paper, setPaper] = useState('A4');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState('');
  const fileInput = useRef<HTMLInputElement>(null);
  const maskRef = useRef<MaskHandle>(null);

  const pickFile = (f?: File | null) => {
    if (!f) return;
    setFile(f);
    setPreviewUrl(URL.createObjectURL(f));
    setError('');
    setStep('operation');
  };

  const process = async () => {
    if (!file) return;
    setError('');
    setStep('processing');
    try {
      const [uploaded] = await imagesApi.upload([file]);
      let result;
      if (op === 'coloring') {
        result = await processingApi.convertToLineArt({ imageId: uploaded.id, algorithm: 'ai', detailLevel: detail });
      } else if (op === 'cleanup') {
        result = await processingApi.cleanup({ imageId: uploaded.id });
      } else {
        const blob = await maskRef.current?.getMask();
        if (!blob) throw new Error('Paint over the watermark first.');
        const maskFile = new File([blob], 'mask.png', { type: 'image/png' });
        result = await processingApi.removeWatermark(uploaded.id, maskFile);
      }
      setResultId(result.id);
      setResultUrl(`${imagesApi.getFileUrl(result.id)}?t=${Date.now()}`);
      setStep('output');
    } catch (e: any) {
      setError(e?.response?.data ?? e?.message ?? 'Something went wrong. Is the AI box reachable?');
      setStep('options');
    }
  };

  const download = async (kind: 'png' | 'svg') => {
    if (!resultId) return;
    setBusy(kind);
    try {
      const name = (file?.name || 'coloring-page').replace(/\.[^.]+$/, '');
      if (kind === 'png') await processingApi.downloadPng(resultId, name, { paper });
      else await processingApi.downloadSvg(resultId, name);
    } catch (e: any) {
      setError(e?.message ?? 'Download failed');
    } finally {
      setBusy('');
    }
  };

  const reset = () => {
    setStep('upload');
    setFile(null);
    setPreviewUrl('');
    setResultId(null);
    setResultUrl('');
    setError('');
  };

  const chooseOp = (o: Operation) => {
    setOp(o);
    setStep('options');
  };

  const steps: Step[] = ['upload', 'operation', 'options', 'processing', 'output'];
  const stepIndex = steps.indexOf(step);

  return (
    <div className="max-w-3xl mx-auto px-4 py-10">
      <h1 className="text-3xl font-bold text-center mb-2 bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent">
        ColorCraft Studio
      </h1>
      <p className="text-center text-gray-500 mb-8">Upload → choose what to do → download.</p>

      <div className="flex justify-center gap-2 mb-10">
        {steps.map((s, i) => (
          <div key={s} className={`h-2 rounded-full transition-all ${i <= stepIndex ? 'bg-indigo-500 w-10' : 'bg-gray-200 w-6'}`} />
        ))}
      </div>

      {error && (
        <div className="mb-6 rounded-lg bg-red-50 border border-red-200 text-red-700 px-4 py-3 text-sm">{String(error)}</div>
      )}

      {/* STEP 1: UPLOAD */}
      {step === 'upload' && (
        <div
          onClick={() => fileInput.current?.click()}
          onDragOver={(e) => e.preventDefault()}
          onDrop={(e) => {
            e.preventDefault();
            pickFile(e.dataTransfer.files?.[0]);
          }}
          className="cursor-pointer border-2 border-dashed border-indigo-300 rounded-2xl p-16 text-center bg-white hover:bg-indigo-50/50 transition-colors"
        >
          <div className="text-5xl mb-4">📷</div>
          <p className="text-lg font-medium text-gray-700">Drop an image here, or click to choose</p>
          <p className="text-gray-400 text-sm mt-1">A photo, or an existing drawing/coloring image.</p>
          <input ref={fileInput} type="file" accept="image/*" className="hidden" onChange={(e) => pickFile(e.target.files?.[0])} />
        </div>
      )}

      {/* STEP 2: OPERATION */}
      {step === 'operation' && (
        <div>
          {previewUrl && <img src={previewUrl} alt="your upload" className="mx-auto max-h-48 rounded-xl shadow mb-8 object-contain" />}
          <p className="text-center font-medium text-gray-700 mb-4">What do you want to do?</p>
          <div className="grid sm:grid-cols-3 gap-4 mb-8">
            {OPERATIONS.map((o) => (
              <button
                key={o.id}
                onClick={() => chooseOp(o.id)}
                className="text-left rounded-xl border-2 border-gray-200 bg-white p-4 transition-all hover:border-indigo-400 hover:shadow"
              >
                <div className="text-2xl mb-1">{o.emoji}</div>
                <div className="font-semibold text-gray-800">{o.title}</div>
                <div className="text-sm text-gray-500">{o.blurb}</div>
              </button>
            ))}
          </div>
          <div className="flex justify-center">
            <button onClick={reset} className="px-5 py-2.5 rounded-lg text-gray-600 hover:bg-gray-100 font-medium">← Change image</button>
          </div>
        </div>
      )}

      {/* STEP 3: OPTIONS (per operation) */}
      {step === 'options' && (
        <div>
          {op === 'coloring' && (
            <>
              <p className="text-center font-medium text-gray-700 mb-4">How much detail?</p>
              <div className="grid sm:grid-cols-3 gap-4 mb-8">
                {DETAILS.map((d) => (
                  <button
                    key={d.id}
                    onClick={() => setDetail(d.id)}
                    className={`text-left rounded-xl border-2 p-4 transition-all ${
                      detail === d.id ? 'border-indigo-500 bg-indigo-50 shadow' : 'border-gray-200 bg-white hover:border-indigo-300'
                    }`}
                  >
                    <div className="text-2xl mb-1">{d.emoji}</div>
                    <div className="font-semibold text-gray-800">{d.title}</div>
                    <div className="text-sm text-gray-500">{d.blurb}</div>
                  </button>
                ))}
              </div>
            </>
          )}

          {op === 'cleanup' && (
            <div className="text-center mb-8">
              {previewUrl && <img src={previewUrl} alt="" className="mx-auto max-h-56 rounded-xl shadow mb-4 object-contain" />}
              <p className="text-gray-600">We'll tidy this drawing into crisp black-and-white line art.</p>
            </div>
          )}

          {op === 'watermark' && (
            <div className="mb-8">
              <p className="text-center font-medium text-gray-700 mb-2">Paint over the watermark</p>
              <p className="text-center text-gray-400 text-sm mb-4">Brush over the area you want erased (it'll show red).</p>
              {previewUrl && <MaskPainter ref={maskRef} imageUrl={previewUrl} />}
            </div>
          )}

          <div className="flex justify-center gap-3">
            <button onClick={() => setStep('operation')} className="px-5 py-2.5 rounded-lg text-gray-600 hover:bg-gray-100 font-medium">← Back</button>
            <button
              onClick={process}
              className="px-8 py-2.5 rounded-lg bg-gradient-to-r from-indigo-500 to-purple-500 text-white font-medium hover:shadow-lg hover:shadow-indigo-500/25 transition-all"
            >
              {op === 'coloring' ? 'Create coloring page →' : op === 'cleanup' ? 'Clean it up →' : 'Remove watermark →'}
            </button>
          </div>
        </div>
      )}

      {/* STEP 4: PROCESSING */}
      {step === 'processing' && (
        <div className="text-center py-16">
          <div className="inline-block w-12 h-12 border-4 border-indigo-200 border-t-indigo-500 rounded-full animate-spin mb-6" />
          <p className="text-lg font-medium text-gray-700">Working on it…</p>
          <p className="text-gray-400 text-sm mt-1">The AI is processing — this usually takes 30–60 seconds.</p>
        </div>
      )}

      {/* STEP 5: OUTPUT */}
      {step === 'output' && (
        <div>
          {resultUrl && <img src={resultUrl} alt="result" className="mx-auto max-h-80 rounded-xl shadow border bg-white mb-8 object-contain" />}
          <div className="bg-white rounded-2xl border p-6 shadow-sm">
            <p className="font-medium text-gray-700 mb-3">Download</p>
            <div className="flex flex-wrap items-end gap-4">
              <div>
                <label className="block text-xs text-gray-500 mb-1">Paper size</label>
                <select value={paper} onChange={(e) => setPaper(e.target.value)} className="border border-gray-300 rounded-lg px-3 py-2 bg-white">
                  {PAPERS.map((p) => (
                    <option key={p} value={p}>{p}</option>
                  ))}
                </select>
              </div>
              <button onClick={() => download('png')} disabled={busy !== ''} className="px-6 py-2 rounded-lg bg-indigo-500 text-white font-medium hover:bg-indigo-600 disabled:opacity-50">
                {busy === 'png' ? 'Preparing…' : 'Download PNG'}
              </button>
              <button onClick={() => download('svg')} disabled={busy !== ''} className="px-6 py-2 rounded-lg border border-indigo-300 text-indigo-600 font-medium hover:bg-indigo-50 disabled:opacity-50">
                {busy === 'svg' ? 'Preparing…' : 'Download SVG'}
              </button>
            </div>
            <p className="text-xs text-gray-400 mt-3">PNG prints at your chosen paper size (300 DPI). SVG is infinitely scalable.</p>
          </div>
          <div className="text-center mt-8">
            <button onClick={reset} className="px-6 py-2.5 rounded-lg text-indigo-600 hover:bg-indigo-50 font-medium">+ Make another</button>
          </div>
        </div>
      )}
    </div>
  );
}
